import { translations, type LocaleKey } from '../i18n';

const getCurrentLocale = (): LocaleKey => {
  try {
    const stored = localStorage.getItem('locale');
    if (stored === 'en' || stored === 'tr') {
      return stored;
    }
  } catch {
    // ignore
  }

  if (typeof document !== 'undefined') {
    const lang = String(document.documentElement.lang ?? '').toLowerCase();
    if (lang.startsWith('en')) return 'en';
    if (lang.startsWith('tr')) return 'tr';
  }

  return 'tr';
};

export const translateAuthMessage = (key: string) => {
  const locale = getCurrentLocale();
  return translations[locale]?.[key] ?? translations.tr?.[key] ?? key;
};

const isInactiveUserMessage = (message: string) => {
  const normalized = message.toLowerCase();
  return (
    normalized.includes('pasif kullanıcı') ||
    normalized.includes('pasif durumda') ||
    normalized.includes('inactive user') ||
    normalized.includes('inactive account')
  );
};

const normalizeAuthMessage = (message: string) =>
  message
    .toLowerCase()
    .replaceAll('ı', 'i')
    .replaceAll('İ', 'i')
    .replaceAll('ü', 'u')
    .replaceAll('Ü', 'u')
    .replaceAll('ö', 'o')
    .replaceAll('Ö', 'o')
    .replaceAll('ç', 'c')
    .replaceAll('Ç', 'c')
    .replaceAll('ş', 's')
    .replaceAll('Ş', 's')
    .replaceAll('ğ', 'g')
    .replaceAll('Ğ', 'g')
    .trim();

const isDuplicateEmailMessage = (message: string) => {
  const normalized = normalizeAuthMessage(message);
  return (
    normalized.includes('e-posta ile kayit mevcut') ||
    normalized.includes('email ile kayit mevcut') ||
    normalized.includes('account already exists with this email') ||
    normalized.includes('email already exists')
  );
};

const isDuplicatePhoneMessage = (message: string) => {
  const normalized = normalizeAuthMessage(message);
  return (
    normalized.includes('telefon numarasi ile kayit mevcut') ||
    normalized.includes('telefon ile kayit mevcut') ||
    normalized.includes('phone number already exists') ||
    normalized.includes('account already exists with this phone number')
  );
};

const extractBackendMessage = (error: any) => {
  return String(error?.response?.data?.message ?? error?.message ?? '').trim();
};

export const isRoleSelectionError = (error: any) => {
  const status = error?.response?.status;
  const normalizedMessage = normalizeAuthMessage(extractBackendMessage(error));

  if (status !== 400 && status !== 401 && status !== 403) {
    return false;
  }

  return (
    normalizedMessage.includes('rolunuzu kontrol ediniz') ||
    normalizedMessage.includes('rolunuzu kontrol edin') ||
    normalizedMessage.includes('bu hesap turu ile giris yapamazsiniz') ||
    normalizedMessage.includes('bu hesap turu ile giris yapilamaz') ||
    normalizedMessage.includes('check role')
  );
};

export const resolveLoginErrorMessage = (error: any, loginRole?: string) => {
  const status = error?.response?.status;
  const backendMessage = extractBackendMessage(error);
  const normalizedMessage = backendMessage.toLowerCase();
  const isPrivilegedRole = loginRole === 'admin' || loginRole === 'manager';

  if (status === 422 && isInactiveUserMessage(backendMessage)) {
    return 'auth.login.inactiveUser';
  }

  if (isPrivilegedRole) {
    if (status === 401 || status === 403) {
      return 'auth.login.checkRole';
    }
    if (
      status === 400 &&
      (normalizedMessage.includes('validation failed') ||
        normalizedMessage.includes('bad request') ||
        normalizedMessage.includes('rol'))
    ) {
      return 'auth.login.checkRole';
    }
  }

  return String(error?.response?.data?.message ?? error?.message ?? 'İşlem başarısız.');
};

export const resolveRegisterErrorMessage = (error: any) => {
  const backendMessage = extractBackendMessage(error);
  const status = error?.response?.status;

  if ((status === 409 || status === 422) && isInactiveUserMessage(backendMessage)) {
    return 'auth.register.inactiveUser';
  }

  if (status === 409 || status === 422) {
    if (isDuplicateEmailMessage(backendMessage)) {
      return 'auth.register.emailExists';
    }
    if (isDuplicatePhoneMessage(backendMessage)) {
      return 'auth.register.phoneExists';
    }
  }

  return backendMessage || 'İşlem başarısız.';
};

export const renderAuthErrorMessage = (message: string | undefined | null, translate: (key: string) => string) => {
  const value = String(message ?? '').trim();
  if (!value) return '';
  if (value.startsWith('auth.')) {
    return translate(value);
  }
  return value;
};
